import React, { useEffect, useMemo } from 'react'
import ClientInformation from '../HourlyInvoice/ClientInformation'

/**
 * Step 2 — Update Invoice fields. Mirrors Scale's
 * UpdateInvoice/FixedUpdateInvoice/Step2.jsx structure exactly:
 *   - "Applies to: {Party}" header + "Current amount: $XXX" line
 *   - Client Information cards (same component as Create Invoice's
 *     ClientInformation — read view by default with pencil → edit mode)
 *   - "Line Items" teal heading
 *   - Description / Amount table with + Add Item inline
 *   - Subtotal row inside the table card
 *   - "Include Credit Card Processing Fee" toggle switch
 *   - "This invoice will be split evenly…" note when multiple parties
 *     are selected
 *
 * Widened from the fixed two-slot (plaintiff/defendant) shape to a party
 * LIST: `selectedParties` carries one entry per party this edit currently
 * targets (one for a single-party edit, N for "All parties"), each keyed by
 * client_id — not by two hardcoded slots — so a card is rendered per
 * SELECTED party rather than exactly two.
 *
 * Validations (Scale parity):
 *   - At least one item with valid amount (subtotal > 0)
 *   - Every item has a description and amount > 0
 *   - Cannot remove first item (index 0); cannot remove last item
 */

function fmtMoney(n) {
    const v = Number(n) || 0
    return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/* Switcher — minimal styled toggle, matches Scale's `Switcher` component
 * visually (rounded pill, teal when on, gray when off). */
function ToggleSwitch({ checked, onChange, disabled }) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            disabled={disabled}
            onClick={() => !disabled && onChange(!checked)}
            className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
                checked ? 'bg-[#115E59]' : 'bg-gray-300'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        >
            <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                    checked ? 'translate-x-[18px]' : 'translate-x-0.5'
                }`}
            />
        </button>
    )
}

export default function Step2UpdateInvoice({
    // One entry per party this edit currently targets:
    // { client_id, label, party, invoice }. Length 1 for a single-party
    // edit, N for "All parties".
    selectedParties = [],
    infoByClientId = {},
    setInfoFor,
    items,
    setItems,
    includeProcessingFee,
    disableProcessingFee = false,
    setIncludeProcessingFee,
    currentAmount = 0,
    error,
    setError,
}) {
    // Auto-init: at least one item with description+amount
    useEffect(() => {
        if (!items || items.length === 0) {
            setItems([{ id: 1, description: 'Mediation Session', amount: 0 }])
        }
    // eslint-disable-next-line
    }, [])

    const subtotal = useMemo(
        () => (items || []).reduce((s, it) => s + (Number(it.amount) || 0), 0),
        [items]
    )

    const updateItem = (idx, patch) => {
        setItems(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)))
        if (error) setError(null)
    }

    const addItem = () => {
        const maxId = items.reduce((m, it) => Math.max(m, Number(it.id) || 0), 0)
        setItems([...items, { id: maxId + 1, description: '', amount: 0 }])
    }

    const removeItem = (idx) => {
        // Scale parity (FixedUpdateInvoice/Step2.jsx:1385–1403): block removal
        // of the FIRST item (idx === 0) AND of the last item (length === 1).
        if (idx === 0) {
            setError('Cannot remove the first item. At least one line item is required.')
            return
        }
        if (items.length <= 1) {
            setError('Cannot remove the last item. Please add another item first.')
            return
        }
        setItems(items.filter((_, i) => i !== idx))
        if (error) setError(null)
    }

    const selectedPartyCount = selectedParties.length
    const isBoth = selectedPartyCount > 1

    const appliesTo = selectedPartyCount > 1
        ? `${selectedPartyCount} Parties`
        : (selectedParties[0]?.label || '—')

    /* Build the `client` shape ClientInformation expects: a flat object
     * with name/email/phone/address (and optional invoice_emails). The
     * onChange callback receives `{ client_information: {...}, invoice_emails }`
     * which we map back to our flat info state. */
    const buildClient = (party, info) => ({
        client_id: party?.client_id ?? party?.clientId,
        name: info?.name ?? party?.name ?? '',
        email: info?.email ?? party?.email ?? '',
        phone: info?.phone ?? party?.phone ?? party?.phone_number ?? '',
        address: info?.address ?? party?.address ?? '',
        role: party?.role,
        invoice_emails: info?.invoice_emails
            ? (typeof info.invoice_emails === 'string'
                ? info.invoice_emails.split(',').map((s) => s.trim()).filter(Boolean)
                : info.invoice_emails)
            : [],
    })

    // Single handler, parameterized by client_id — replaces the old
    // handlePlaintiffChange/handleDefendantChange pair so any number of
    // selected parties can edit their own card independently.
    const handleClientChange = (clientId) => (_clientId, patch) => {
        const ci = patch?.client_information || {}
        setInfoFor(clientId, (prev) => ({
            ...prev,
            ...(ci.name !== undefined ? { name: ci.name } : {}),
            ...(ci.email !== undefined ? { email: ci.email } : {}),
            ...(ci.phone_number !== undefined ? { phone: ci.phone_number } : {}),
            ...(ci.address !== undefined ? { address: ci.address } : {}),
            ...(patch?.invoice_emails !== undefined ? { invoice_emails: patch.invoice_emails } : {}),
        }))
    }

    return (
        <div className="space-y-5">
            <div>
                <p className="font-semibold text-gray-900 mb-1">
                    Applies to: {appliesTo}
                </p>
                <p className="text-sm text-gray-500">
                    Current amount: <span className="font-semibold">${fmtMoney(currentAmount)}</span>
                </p>
            </div>

            {/* Client Information — Scale parity uses the same ClientInformation
                component as Create Invoice (read view + edit pencil). One card
                per SELECTED party, not two fixed slots. */}
            <div className={`grid gap-4 ${isBoth ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
                {selectedParties.map((sp) => (
                    <ClientInformation
                        key={sp.client_id}
                        client={buildClient(sp.party, infoByClientId[sp.client_id])}
                        label={sp.label}
                        onClientChange={handleClientChange(sp.client_id)}
                    />
                ))}
            </div>

            {/* Line Items section — teal heading + table with Add Item inline */}
            <div className="space-y-2">
                <p className="font-semibold text-teal-800">Line Items</p>
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-100 border-b border-gray-200">
                            <tr>
                                <th className="py-2 px-5 text-left font-semibold text-gray-700">Description</th>
                                <th className="py-2 pr-5 text-right font-semibold text-gray-700 w-40">Amount</th>
                                <th className="py-2 px-3 w-10"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items && items.length > 0 ? items.map((it, i) => (
                                <tr key={it.id || i} className="border-b border-gray-200">
                                    <td className="px-5 py-3">
                                        <input
                                            type="text"
                                            placeholder="eg. Mediation Session"
                                            value={it.description}
                                            onChange={(e) => updateItem(i, { description: e.target.value })}
                                            className="w-full rounded-md border border-gray-300 focus:outline-none focus:border-teal-600 px-2 py-1.5"
                                        />
                                    </td>
                                    <td className="pr-5 py-3 text-right">
                                        <div className="flex justify-end">
                                            <div className="relative">
                                                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                                                <input
                                                    type="number"
                                                    min={0}
                                                    step="0.01"
                                                    value={it.amount === 0 ? '' : it.amount}
                                                    onFocus={(e) => {
                                                        if (it.amount === 0 || it.amount === '0') e.target.select()
                                                    }}
                                                    onChange={(e) => {
                                                        let v = e.target.value
                                                        if (v === '' || v === '.') {
                                                            updateItem(i, { amount: 0 })
                                                            return
                                                        }
                                                        // Strip leading zeros (Scale parity)
                                                        if (/^0+[1-9]/.test(v)) v = v.replace(/^0+/, '')
                                                        if (v.includes('.')) {
                                                            const [int, frac] = v.split('.')
                                                            v = int + '.' + (frac || '').slice(0, 2)
                                                        }
                                                        const num = parseFloat(v)
                                                        updateItem(i, { amount: isNaN(num) ? 0 : num })
                                                    }}
                                                    className="w-[120px] rounded-md border border-gray-300 text-center pl-[18px] py-1 focus:outline-none focus:border-teal-600"
                                                />
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-3 text-center">
                                        {/* Scale parity: hide delete on first row, also hide if only one item. */}
                                        {i > 0 && items.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => removeItem(i)}
                                                className="text-gray-400 hover:text-red-500 transition-colors"
                                                title="Remove item"
                                            >
                                                <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor">
                                                    <path d="M6 2a1 1 0 00-1 1v1H3a1 1 0 100 2h14a1 1 0 100-2h-2V3a1 1 0 00-1-1H6zm-1 6a1 1 0 011-1h8a1 1 0 011 1v9a2 2 0 01-2 2H7a2 2 0 01-2-2V8z"/>
                                                </svg>
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={3} className="px-5 py-8 text-center text-gray-500">
                                        No items found
                                    </td>
                                </tr>
                            )}
                            {/* + Add Item — inline at bottom of table body, Scale parity */}
                            {items && items.length > 0 && (
                                <tr>
                                    <td colSpan={3} className="px-5">
                                        <button
                                            type="button"
                                            onClick={addItem}
                                            className="my-2 inline-flex items-center gap-1.5 font-semibold text-teal-800 hover:text-teal-900"
                                        >
                                            <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                                                <path d="M10 2a8 8 0 100 16 8 8 0 000-16zm0 14a6 6 0 110-12 6 6 0 010 12zm1-9a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"/>
                                            </svg>
                                            Add Item
                                        </button>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                    <div className="bg-gray-50 px-5 py-2.5 border-t border-gray-200 flex justify-between items-center">
                        <p className="font-semibold text-base text-gray-900">Subtotal:</p>
                        <p className="text-base font-semibold text-[#027776] pr-[60px]">${fmtMoney(subtotal)}</p>
                    </div>
                </div>
            </div>

            {/* Processing fee toggle — the mediator portal removes this entirely on
                the fixed-invoice edit (DISABLE_FIXED_UPDATE_PROCESSING_FEE). Hidden
                here for the same reason: in gateway mode the card fee belongs at
                checkout, not on the invoice, and writing the column from this screen
                also changes which link the invoice email carries. */}
            {!disableProcessingFee && (
            <div className="space-y-2">
                <div className="flex items-center gap-3">
                    <ToggleSwitch
                        checked={!!includeProcessingFee}
                        onChange={(v) => setIncludeProcessingFee(v)}
                    />
                    <label
                        className="font-semibold text-gray-900 cursor-pointer select-none"
                        onClick={() => setIncludeProcessingFee(!includeProcessingFee)}
                    >
                        Include Credit Card Processing Fee
                    </label>
                </div>
                {includeProcessingFee && (
                    <p className="ml-[51px] text-sm text-gray-600">
                        Processing fee will be calculated automatically based on the subtotal.
                    </p>
                )}
            </div>
            )}

            {error ? (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600">{error}</p>
                </div>
            ) : null}

            {isBoth && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-800">
                        <strong>Note: </strong>
                        {/* Driven by the count and identity of selected parties, not a
                            hardcoded "50/50 … plaintiff and defendant" — this reads
                            correctly whether it's the primary two, or N parties
                            (including additional parties) selected via "All parties". */}
                        {selectedPartyCount === 2
                        && selectedParties.some((p) => p.side === 'plaintiff' && p.isPrimary)
                        && selectedParties.some((p) => p.side === 'defendant' && p.isPrimary)
                            ? 'This invoice will be split evenly between the plaintiff and defendant.'
                            : `This invoice will be split evenly across the ${selectedPartyCount} selected parties.`}
                    </p>
                </div>
            )}
        </div>
    )
}
